import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { SquareAccountService } from '../services/square-account.service';
import * as SquareAccountActions from './square-account.actions';

@Injectable()
export class SquareAccountEffects {
    private _actions$ = inject(Actions);
    private _squareAccountService = inject(SquareAccountService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all square accounts',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All
    getAllSquareAccounts$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareAccountActions.loadSquareAccounts),
            switchMap(() =>
                this._squareAccountService.getAll().pipe(
                    map((res) =>
                        SquareAccountActions.loadSquareAccountsSuccess({
                            squareAccounts: res.data.squareAccounts,
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareAccountActions.loadSquareAccountsFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Get One
    getSquareAccount$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareAccountActions.loadSquareAccount),
            switchMap((action) =>
                this._squareAccountService.getOne(action.id).pipe(
                    map((res) =>
                        SquareAccountActions.loadSquareAccountSuccess({
                            squareAccount: res.data,
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareAccountActions.loadSquareAccountFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Create
    createSquareAccount$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareAccountActions.createSquareAccount),
            switchMap((action) =>
                this._squareAccountService.create(action.data).pipe(
                    map((res) =>
                        SquareAccountActions.createSquareAccountSuccess({
                            squareAccount: res.data,
                            success: 'Square account created successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareAccountActions.createSquareAccountFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Update
    updateSquareAccount$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareAccountActions.updateSquareAccount),
            switchMap((action) =>
                this._squareAccountService.update(action.data, action.id).pipe(
                    map((res) =>
                        SquareAccountActions.updateSquareAccountSuccess({
                            squareAccount: res.data,
                            success: 'Square account updated successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareAccountActions.updateSquareAccountFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Delete
    deleteSquareAccount$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareAccountActions.deleteSquareAccount),
            switchMap((action) =>
                this._squareAccountService.delete(action.id).pipe(
                    map(() =>
                        SquareAccountActions.deleteSquareAccountSuccess({
                            id: action.id,
                            success: 'Square account deleted successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareAccountActions.deleteSquareAccountFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Get Merchant By Token
    getMerchantByToken$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareAccountActions.getMerchantByToken),
            switchMap((action) =>
                this._squareAccountService
                    .getMerchantByToken({
                        token: action.token,
                        environment: action.environment,
                    })
                    .pipe(
                        map((res) =>
                            SquareAccountActions.getMerchantByTokenSuccess({
                                merchant: res.data,
                            })
                        ),
                        catchError((error) =>
                            of(
                                SquareAccountActions.getMerchantByTokenFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    )
            )
        )
    );

    // Get Locations By Token
    getLocationsByToken$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareAccountActions.getLocationsByToken),
            switchMap((action) =>
                this._squareAccountService
                    .getLocationsByToken({
                        accessToken: action.accessToken,
                        environment: action.environment,
                    })
                    .pipe(
                        map((res) =>
                            SquareAccountActions.getLocationsByTokenSuccess({
                                locations: res.data,
                            })
                        ),
                        catchError((error) =>
                            of(
                                SquareAccountActions.getLocationsByTokenFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Side Effects (alerts)
    // -----------------------------------------------------------

    onSquareAccountActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareAccountActions.createSquareAccountSuccess,
                    SquareAccountActions.updateSquareAccountSuccess,
                    SquareAccountActions.deleteSquareAccountSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onSquareAccountActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareAccountActions.loadSquareAccountsFailure,
                    SquareAccountActions.loadSquareAccountFailure,
                    SquareAccountActions.createSquareAccountFailure,
                    SquareAccountActions.updateSquareAccountFailure,
                    SquareAccountActions.deleteSquareAccountFailure,
                    SquareAccountActions.getMerchantByTokenFailure,
                    SquareAccountActions.getLocationsByTokenFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
